import { Grid } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { getIn, useFormikContext } from "formik";
import { StyledCommonForms } from "../../../styled/form";
import { StyledGoogleMaps } from "../../../styled/googleMaps";
import { FormikCheckbox, FormikSelect } from "@accessinformatica/formik-mui";
import DeferredFormikTextField from "./deferredFormikTextField";
import {
  catalogSelectMenuItems,
  mapCatalogToOptions,
} from "./catalogMenuItems.jsx";

const TramoGestionFields = ({
  namePrefix,
  disabled,
  tiposTransporte = [],
  motivosTraslado = [],
  agencias = [],
  prestadoresTraslado = [],
  afterField,
  requiredMarks,
  showHelpers,
  showAgencia = true,
  hideMotivo = false,
}) => {
  const formik = useFormikContext();
  const values = formik?.values;
  const setFieldValue = formik?.setFieldValue;
  const sectionValues = getIn(values, namePrefix) || {};
  const currentPrestadorId = sectionValues.idPrestadorTraslado;
  const currentAgenciaId = sectionValues.idAgencia;
  const prevAgenciaIdRef = useRef(currentAgenciaId);

  useEffect(() => {
    if (!showAgencia || !setFieldValue) return;
    const agenciaChanged = String(prevAgenciaIdRef.current ?? "") !== String(currentAgenciaId ?? "");
    prevAgenciaIdRef.current = currentAgenciaId;

    const hasAgencia =
      currentAgenciaId !== "" &&
      currentAgenciaId != null &&
      String(currentAgenciaId) !== "0";
    const hasPrestador =
      currentPrestadorId !== "" &&
      currentPrestadorId != null &&
      String(currentPrestadorId) !== "0";

    if (hasAgencia && (agenciaChanged || !hasPrestador)) {
      const matched = (agencias || []).find(
        (a) => String(a.id) === String(currentAgenciaId),
      );
      if (
        matched?.idPrestador &&
        String(currentPrestadorId) !== String(matched.idPrestador)
      ) {
        setFieldValue(
          `${namePrefix}.idPrestadorTraslado`,
          String(matched.idPrestador),
          false,
        );
      }
    }
  }, [
    currentAgenciaId,
    agencias,
    currentPrestadorId,
    namePrefix,
    setFieldValue,
    showAgencia,
  ]);

  useEffect(() => {
    if (!showAgencia || !setFieldValue) return;
    if (
      currentPrestadorId !== "" &&
      currentPrestadorId != null &&
      String(currentPrestadorId) !== "0" &&
      currentAgenciaId !== "" &&
      currentAgenciaId != null &&
      String(currentAgenciaId) !== "0"
    ) {
      const matched = (agencias || []).find(
        (a) => String(a.id) === String(currentAgenciaId),
      );
      if (
        matched &&
        matched.idPrestador != null &&
        String(matched.idPrestador) !== "0" &&
        String(matched.idPrestador) !== String(currentPrestadorId)
      ) {
        setFieldValue(`${namePrefix}.idAgencia`, 0, false);
      }
    }
  }, [
    currentPrestadorId,
    currentAgenciaId,
    agencias,
    namePrefix,
    setFieldValue,
    showAgencia,
  ]);

  const filteredAgencias = useMemo(() => {
    if (
      !currentPrestadorId ||
      String(currentPrestadorId) === "0" ||
      String(currentPrestadorId) === ""
    ) {
      return agencias;
    }
    return (agencias || []).filter(
      (a) =>
        String(a.id) === "0" ||
        !a.idPrestador ||
        String(a.idPrestador) === String(currentPrestadorId),
    );
  }, [agencias, currentPrestadorId]);

  const tiposTransporteItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(tiposTransporte)),
    [tiposTransporte],
  );
  const motivosTrasladoItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(motivosTraslado)),
    [motivosTraslado],
  );
  const prestadoresItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(prestadoresTraslado)),
    [prestadoresTraslado],
  );
  const agenciasItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(filteredAgencias)),
    [filteredAgencias],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <StyledGoogleMaps.FieldApplyRow>
          <FormikSelect
            disabled={disabled}
            label={requiredMarks ? "Tipo de transporte *" : "Tipo de transporte"}
            name={`${namePrefix}.idTipoTransporte`}
            sx={StyledCommonForms.input}
            fullWidth
            variant="outlined">
            {tiposTransporteItems}
          </FormikSelect>
          {afterField?.("idTipoTransporte")}
        </StyledGoogleMaps.FieldApplyRow>
      </Grid>
      {!hideMotivo && (
        <Grid item xs={12} sm={6}>
          <StyledGoogleMaps.FieldApplyRow>
            <FormikSelect
              disabled={disabled}
              label={requiredMarks ? "Motivo del traslado *" : "Motivo del traslado"}
              name={`${namePrefix}.idMotivoTraslado`}
              sx={StyledCommonForms.input}
              fullWidth
              variant="outlined">
              {motivosTrasladoItems}
            </FormikSelect>
            {afterField?.("idMotivoTraslado")}
          </StyledGoogleMaps.FieldApplyRow>
        </Grid>
      )}
      {showAgencia && (
        <>
          <Grid item xs={12} sm={6}>
            <StyledGoogleMaps.FieldApplyRow>
              <FormikSelect
                disabled={disabled}
                label="Prestador de traslado"
                name={`${namePrefix}.idPrestadorTraslado`}
                sx={StyledCommonForms.input}
                fullWidth
                variant="outlined">
                {prestadoresItems}
              </FormikSelect>
              {afterField?.("idPrestadorTraslado")}
            </StyledGoogleMaps.FieldApplyRow>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledGoogleMaps.FieldApplyRow>
              <FormikSelect
                disabled={disabled}
                helperText={
                  showHelpers
                    ? "Opcional en la carga. Es por tramo: se puede despachar la ida a una agencia y la vuelta a otra."
                    : undefined
                }
                label="Agencia"
                name={`${namePrefix}.idAgencia`}
                sx={StyledCommonForms.input}
                fullWidth
                variant="outlined">
                {agenciasItems}
              </FormikSelect>
              {afterField?.("idAgencia")}
            </StyledGoogleMaps.FieldApplyRow>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <StyledGoogleMaps.FieldApplyRow>
          <div>
            <StyledGoogleMaps.RequerimientosLabel>
              Requerimientos especiales del tramo
            </StyledGoogleMaps.RequerimientosLabel>
            <StyledGoogleMaps.RequerimientosRow>
              <FormikCheckbox
                disabled={disabled}
                label="Pool"
                name={`${namePrefix}.pool`}
              />
              <FormikCheckbox
                disabled={disabled}
                label="Acompañante"
                name={`${namePrefix}.acompanante`}
              />
              <FormikCheckbox
                disabled={disabled}
                label="Silla de ruedas"
                name={`${namePrefix}.sillaRuedas`}
              />
              <FormikCheckbox
                disabled={disabled}
                label="Muletas"
                name={`${namePrefix}.muletas`}
              />
              {afterField?.("requerimientosEspeciales")}
            </StyledGoogleMaps.RequerimientosRow>
          </div>
        </StyledGoogleMaps.FieldApplyRow>
      </Grid>
      <Grid item xs={12}>
        <StyledGoogleMaps.FieldApplyRow>
          <DeferredFormikTextField
            disabled={disabled}
            label="Observaciones del tramo"
            minRows={2}
            multiline
            name={`${namePrefix}.observacionesTramo`}
            sx={StyledCommonForms.input}
            variant="outlined"
          />
          {afterField?.("observacionesTramo")}
        </StyledGoogleMaps.FieldApplyRow>
      </Grid>
    </Grid>
  );
};

export default TramoGestionFields;
